import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import cx from 'classnames';
import { Alert } from 'jl-design-system/components/alert/Alert';

import { AppDispatch, RootState } from 'types/RootState.types';
import appConstants from '@constants/appConstants';
import scrollConstants from '@constants/scrollConstants';
import { isSinglePageCheckoutEnabledFeatureActive } from '@redux/reducers/config/configSelector';
import { getDeliverySinglePageCheckoutStageSelector } from '@redux/reducers/delivery/deliverySelector';
import { getShowPaymentUnavailableMessage } from '@redux/reducers/payment/paymentSelector';
import { setShowPaymentUnavailableMessage } from '@redux/actions/payment/paymentActions';
import scrollToElement from '@utils/scroll/scrollTo';
import Container from '@components/container';
import styles from './fulfilment-confirmation-alert.scss';

const FulfilmentConfirmationAlert = ({
  detailsType,
  className,
  inForm = false,
}: {
  className?: string;
  detailsType: 'delivery' | 'collection';
  inForm?: boolean;
}) => {
  const dispatch: AppDispatch = useDispatch();
  const [formValidationErrorVisible, setFormValidationErrorVisible] = useState(
    () => typeof document !== 'undefined' && !!document.querySelector('[data-testid="form-validation-error-message"]'),
  );
  const showAlert = useSelector(
    (state: RootState) =>
      isSinglePageCheckoutEnabledFeatureActive(state) &&
      getDeliverySinglePageCheckoutStageSelector(state) !== appConstants.PAYMENT_STAGE &&
      getShowPaymentUnavailableMessage(state),
  );
  const shouldShowAlert = showAlert && !formValidationErrorVisible;

  useEffect(() => {
    if (typeof document === 'undefined' || !document.body || typeof MutationObserver === 'undefined') return undefined;

    const updateFormValidationErrorVisibility = () => {
      const errorVisible = !!document.querySelector('[data-testid="form-validation-error-message"]');
      setFormValidationErrorVisible((currentValue) => (currentValue === errorVisible ? currentValue : errorVisible));
    };

    const observer = new MutationObserver(updateFormValidationErrorVisibility);
    observer.observe(document.body, { childList: true, subtree: true });
    updateFormValidationErrorVisibility();

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (formValidationErrorVisible && showAlert) {
      dispatch(setShowPaymentUnavailableMessage(false));
    }
  }, [dispatch, formValidationErrorVisible, showAlert]);

  useEffect(() => {
    if (shouldShowAlert) {
      scrollToElement({
        id: scrollConstants.FULFILMENT_CONFIRMATION_ALERT,
        config: { behavior: 'smooth', block: 'start' },
        respectReducedMotion: true,
      });
    }
  }, [shouldShowAlert]);

  return shouldShowAlert ? (
    <Container className={cx(styles.container, className)} marginBottom={inForm ? undefined : '2'} width="full">
      <div data-scroll={scrollConstants.FULFILMENT_CONFIRMATION_ALERT} tabIndex={-1}>
        <Alert
          body={`Confirm your ${detailsType} details to continue to payment`}
          data-testid="fulfilment-confirmation-alert"
          type="error"
        />
      </div>
    </Container>
  ) : null;
};

export default FulfilmentConfirmationAlert;
